import {
  DoneOutlined,
  RefreshOutlined,
  LockOutlined,
  ErrorOutline,
  SaveAltOutlined,
} from "@mui/icons-material";
import { Box, Button, CircularProgress, Divider, Tooltip } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { merge } from "lodash";
import { useState, useMemo } from "react";
import {
  ControlledLabelSelect,
  ControlledLabelSelectOption,
  makeSxStyles,
  statusColorMap,
} from "ui";
import { RouteUnlockedIcon } from "../../../components/routelockedIcon";
import { Modals } from "../../../state/route-planner-store";
import CustomDatePicker from "../components/custom-date-picker";
import { RouteStatus } from "./helpers/route-status.enum";
import { useFilteredRouteOptions } from "../../../utils/route-status-options";
import {
  AdminRouteV2ControllerGetRoutesResponse,
  useAdminRouteV2ControllerGetRoutesStatusesPerMonth,
} from "service";
import { DateTime } from "luxon";
import HeaderItems, {
  HeaderItemsLeft,
  HeaderItemsRight,
} from "../../../shared/components/header-items/HeaderItems";
import { useEstabrookContext } from "service/api/estabrookContext";
import { usePlowReportDownload } from "../../../hooks/use-plow-report-download";
import { fetchShiftPlowReport } from "../../../service/plow-report";
import SkippedOrdersButton from "../../../features/skipped-orders/components/SkippedOrdersButton";

type RoutePlannerToolbarActionsProps = {
  routesData?: AdminRouteV2ControllerGetRoutesResponse;
  handleRefreshRoute: () => void;
  isRefreshing: boolean;
  openModal: (modal: Modals) => void;
  currentRouteStatus?: RouteStatus;
  routeLocked: boolean;
  renderRoutePhaseButtons: () => React.ReactNode;
  selectedDate: DateTime;
  handleDateChange: (date: DateTime) => void;
  lockRoute: () => void;
};

type RouteStatusesPerMonth = {
  month: number;
  year: number;
};

const RoutePlannerToolbarActions = ({
  routesData,
  handleRefreshRoute,
  isRefreshing,
  openModal,
  currentRouteStatus,
  routeLocked,
  renderRoutePhaseButtons,
  selectedDate,
  handleDateChange,
  lockRoute,
}: RoutePlannerToolbarActionsProps) => {
  const styles = useStyles();
  const { filteredOptions } = useFilteredRouteOptions(routesData);

  const enhancedOptions = useMemo(() => {
    if (currentRouteStatus === RouteStatus.Planned) {
      const planningOption = {
        value: "revert-to-planning",
        label: "Revert to",
        status: "Planning",
      };
      return [planningOption, ...filteredOptions];
    }
    return filteredOptions;
  }, [filteredOptions, currentRouteStatus]);

  const [selectedShift, setSelectedShift] = useState<RouteStatus>(
    RouteStatus.Live,
  );

  const handleChange = (value: string) => {
    if (value === "revert-to-planning") {
      openModal(Modals.REVERT_TO_PLANNING);
      return;
    }
    setSelectedShift(value as RouteStatus);
  };

  const renderOption = (
    option: ControlledLabelSelectOption,
    layoutStyles: React.CSSProperties,
  ) => (
    <Box sx={{ display: "flex", alignItems: "center", ...layoutStyles }}>
      <span>{option.label}</span>
      {option.status && (
        <Box
          sx={{
            padding: "4px 10px",
            borderRadius: "24px",
            backgroundColor: "rgba(197, 198, 201, 0.15)",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            marginLeft: "6px",
          }}
        >
          <span
            style={{
              backgroundColor: statusColorMap[option.status] || "#D3D3D3",
              width: "6px",
              height: "6px",
              borderRadius: "100%",
              marginRight: "7px",
            }}
          ></span>
          {option.status}
        </Box>
      )}
      {option.value === "revert-to-planning" && (
        <ErrorOutline
          sx={{
            marginLeft: "8px",
            color: "#FFE2AB",
          }}
        />
      )}
    </Box>
  );

  const renderSelected = (
    _selectedOption: ControlledLabelSelectOption | undefined,
    layoutStyles: React.CSSProperties,
  ) => {
    // For Planned phase, show the actual "Daily Shift Planned" option instead of "Revert to Planning"
    const availableOption =
      currentRouteStatus === RouteStatus.Planned
        ? filteredOptions[0]
        : enhancedOptions[0];
    return (
      <Box sx={{ display: "flex", alignItems: "center", ...layoutStyles }}>
        <span>{availableOption?.label}</span>
        {availableOption?.status && (
          <Box
            sx={{
              padding: "4px 10px",
              borderRadius: "24px",
              backgroundColor: "rgba(197, 198, 201, 0.15)",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              marginLeft: "6px",
            }}
          >
            <span
              style={{
                backgroundColor:
                  statusColorMap[availableOption.status] || "#D3D3D3",
                width: "6px",
                height: "6px",
                borderRadius: "100%",
                marginRight: "7px",
              }}
            ></span>
            {availableOption.status}
          </Box>
        )}
      </Box>
    );
  };

  const [newMonthYear, setNewMonthYear] = useState<RouteStatusesPerMonth>({
    month: selectedDate.month,
    year: selectedDate.year,
  });

  const { data } = useAdminRouteV2ControllerGetRoutesStatusesPerMonth({
    queryParams: {
      month: newMonthYear.month,
      year: newMonthYear.year,
    },
  });

  const handleMonthChange = (newMonth: DateTime | null) => {
    if (newMonth !== null) {
      setNewMonthYear({ month: newMonth.month, year: newMonth.year });
    }
  };

  const { fetcherOptions } = useEstabrookContext();
  const { download: downloadReport, isLoading: isDownloadingReport } =
    usePlowReportDownload();

  const handleDownloadReport = () => {
    const date = selectedDate.toFormat("yyyy-MM-dd");
    downloadReport(() => fetchShiftPlowReport(fetcherOptions, date));
  };

  return (
    <HeaderItems>
      <HeaderItemsLeft
        breadcrumbs={[
          {
            label: "Route Planner",
          },
        ]}
      >
        <ControlledLabelSelect
          placeholdeShow={false}
          value={selectedShift}
          options={enhancedOptions}
          onChange={handleChange}
          renderOption={renderOption}
          renderSelected={renderSelected}
          layoutStyles={{ justifyContent: "space-between" }}
          sxProp={{ borderRadius: "4px" }}
        />
        {currentRouteStatus === RouteStatus.Live ? (
          <Button
            variant="outlined"
            onClick={() => {
              openModal(Modals.END_SHIFT);
            }}
            startIcon={<DoneOutlined />}
          >
            End Shift
          </Button>
        ) : null}
      </HeaderItemsLeft>
      <HeaderItemsRight>
        {currentRouteStatus === RouteStatus.Ended && (
          <Tooltip title="Download Plow Report">
            <Button
              sx={styles.headerBtn}
              onClick={handleDownloadReport}
              disabled={isDownloadingReport}
              data-testid="download-plow-report"
            >
              {isDownloadingReport ? (
                <CircularProgress size={20} />
              ) : (
                <SaveAltOutlined htmlColor="#8ACEFF" />
              )}
            </Button>
          </Tooltip>
        )}
        <Button
          sx={merge(
            styles.headerBtn,
            isRefreshing && { animation: "rotate 2s linear 2s" },
          )}
          onClick={handleRefreshRoute}
        >
          {isRefreshing ? (
            <CircularProgress size={20} />
          ) : (
            <RefreshOutlined htmlColor="#8ACEFF" />
          )}
        </Button>
        {currentRouteStatus === RouteStatus.Planning && (
          <Button sx={styles.headerBtn} onClick={lockRoute}>
            {routeLocked ? (
              <LockOutlined htmlColor="#8ACEFF" />
            ) : (
              <RouteUnlockedIcon width={25} height={25} />
            )}
          </Button>
        )}
        <Divider
          sx={{ height: "18px" }}
          orientation="vertical"
          variant="middle"
        />
        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <CustomDatePicker
            defaultDate={selectedDate}
            onMonthChange={handleMonthChange}
            onDateChange={(value) => value && handleDateChange(value)}
            highlightedDays={data?.data}
          />
        </LocalizationProvider>
        {currentRouteStatus === RouteStatus.Planning && (
          <SkippedOrdersButton date={selectedDate.toFormat("yyyy-MM-dd")} />
        )}
        {renderRoutePhaseButtons()}
      </HeaderItemsRight>
    </HeaderItems>
  );
};

const useStyles = makeSxStyles(() => ({
  headerActions: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    marginX: "30px",
    width: "100%",
    alignItems: "center",
  },
  routeOperations: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "16px",
  },

  headerBtn: {
    minWidth: "38px",
    maxWidth: "38px",
    borderRadius: "100%",
    height: "38px",
    display: "flex",
    alignItems: "center",
    padding: 0,
  },
}));

export default RoutePlannerToolbarActions;
